import {Transition} from 'history'
import React, {useCallback, useEffect, useState} from 'react'
import {useBlocker, useLocation, useNavigate} from 'react-router-dom'
import {ButtonGroup, FormError} from '../components/form'
import {Button} from '../components/library/buttons'
import {Modal} from '../components/library/modal/modal'

const useCallbackPrompt = (
  when: boolean
): [boolean, () => void, () => void] => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showPrompt, setShowPrompt] = useState(false)
  const [lastLocation, setLastLocation] = useState<Transition>()
  const [confirmedNavigation, setConfirmedNavigation] = useState(false)

  const cancelNavigation = useCallback(() => {
    setShowPrompt(false)
  }, [])

  const handleBlockedNavigation: (tx: Transition) => void = useCallback(
    nextLocation => {
      if (
        !confirmedNavigation &&
        nextLocation.location.pathname !== location.pathname
      ) {
        setShowPrompt(true)
        setLastLocation(nextLocation)
        return false
      }
      return true
    },
    [confirmedNavigation, location]
  )

  const confirmNavigation = useCallback(() => {
    setShowPrompt(false)
    setConfirmedNavigation(true)
  }, [])

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      navigate(lastLocation.location.pathname)
    }
  }, [confirmedNavigation, lastLocation, navigate])

  useBlocker(handleBlockedNavigation, when)

  return [showPrompt, confirmNavigation, cancelNavigation]
}

export function AlertDialog({
  isBlocking,
  title = 'You have unsaved changes',
  saveText = 'Save',
  cancelText = 'Cancel navigation',
  discardText = 'Discard changes',
  onSave,
  onDiscard,
  children
}: {
  isBlocking: boolean
  title?: string
  saveText?: string
  cancelText?: string
  discardText?: string
  onSave?: () => Promise<void>
  onDiscard?: () => void
  children?: React.ReactNode
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isBlocking)
  return showPrompt ? (
    <Modal handleClose={cancelNavigation} title={title}>
      <form
        onSubmit={async e => {
          e.preventDefault()
          setError(undefined)
          if (onSave) {
            try {
              setLoading(true)
              await onSave()
              confirmNavigation()
            } catch (err) {
              if (err instanceof Error) {
                setError(err.message) // object is type unknown
              }
              if (typeof err === 'string') {
                setError(err)
              }
            } finally {
              setLoading(false)
            }
          } else {
            cancelNavigation()
          }
        }}
      >
        {error ? <FormError>{error}</FormError> : null}
        {children}
        <ButtonGroup>
          <Button type="submit" size="large" disabled={loading}>
            {onSave ? saveText : cancelText}
          </Button>
          <Button
            onClick={() => {
              if (onDiscard) {
                onDiscard()
              }
              confirmNavigation()
            }}
            appearance="tint"
            size="large"
          >
            {discardText}
          </Button>
        </ButtonGroup>
      </form>
    </Modal>
  ) : null
}
